import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

if (!window.__drawerUpsellHandlersBound) {
  window.__drawerUpsellHandlersBound = true;

  const getDrawerSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.getAttribute('data-section-id'))
      .filter(Boolean);

  const notifyCartUpdate = (sections) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        detail: {
          sourceId: 'drawer-upsell',
          data: { sections: sections || {} },
        },
      })
    );
  };

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const select = target.closest('[data-drawer-upsell-variant]');
      if (!(select instanceof HTMLSelectElement)) return;

      const root = select.closest('[data-drawer-upsell]');
      if (!root) return;

      const button = root.querySelector('[data-drawer-upsell-add]');
      const price = root.querySelector('[data-drawer-upsell-price]');
      const comparePrice = root.querySelector('[data-drawer-upsell-compare]');
      const currentPrice = root.querySelector('[data-drawer-upsell-current]');
      const option = select.options[select.selectedIndex];

      if (button instanceof HTMLElement) {
        button.setAttribute('data-variant-id', select.value);
      }

      if (
        comparePrice instanceof HTMLElement &&
        currentPrice instanceof HTMLElement &&
        option?.dataset.comparePrice &&
        option?.dataset.salePrice
      ) {
        comparePrice.textContent = option.dataset.comparePrice;
        currentPrice.textContent = option.dataset.salePrice;
      } else if (price instanceof HTMLElement && option?.dataset.price) {
        price.textContent = option.dataset.price;
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const choice = target.closest('[data-drawer-upsell-choice]');
      if (!(choice instanceof HTMLButtonElement)) return;

      const root = choice.closest('[data-drawer-upsell]');
      if (!root) return;

      event.preventDefault();

      root.querySelectorAll('[data-drawer-upsell-choice]').forEach((element) => {
        element.classList.toggle('active', element === choice);
      });

      const button = root.querySelector('[data-drawer-upsell-add]');
      const comparePrice = root.querySelector('[data-drawer-upsell-compare]');
      const currentPrice = root.querySelector('[data-drawer-upsell-current]');

      if (button instanceof HTMLElement && choice.dataset.variantId) {
        button.setAttribute('data-variant-id', choice.dataset.variantId);
      }

      if (
        comparePrice instanceof HTMLElement &&
        currentPrice instanceof HTMLElement &&
        choice.dataset.comparePrice &&
        choice.dataset.salePrice
      ) {
        comparePrice.textContent = choice.dataset.comparePrice;
        currentPrice.textContent = choice.dataset.salePrice;
      }
    },
    true
  );

  document.addEventListener(
    'click',
    async (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('[data-drawer-upsell-add]');
      if (!(button instanceof HTMLButtonElement)) return;

      event.preventDefault();

      if (button.dataset.loading === 'true') return;

      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      const originalText = button.textContent;
      button.dataset.loading = 'true';
      button.disabled = true;
      button.textContent = 'Tilføjer...';

      try {
        const sectionIds = getDrawerSectionIds();
        const upgradeMode = button.dataset.upgradeMode;
        const quantity = Number(button.dataset.upgradeQuantity || '1') || 1;

        if (upgradeMode === 'bundle-swap') {
          const removeKey = button.dataset.upgradeRemoveKey;
          if (!removeKey) {
            throw new Error('Mangler line item key til bundle-opgradering');
          }

          const removeResponse = await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: removeKey,
              quantity: 0,
            }),
          });

          const removeData = await removeResponse.json();

          if (!removeResponse.ok || removeData.status) {
            throw new Error(removeData.description || removeData.message || 'Kunne ikke fjerne eksisterende bundle');
          }

          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity }],
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          });

          const addData = await addResponse.json();

          if (!addResponse.ok || addData.status) {
            throw new Error(addData.description || addData.message || 'Kunne ikke opgradere til Løbepakken');
          }

          notifyCartUpdate(addData.sections);
        } else {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          });

          const data = await response.json();

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Kunne ikke tilføje produkt');
          }

          notifyCartUpdate(data.sections);
        }

        button.textContent = 'Tilføjet';

        window.setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
          button.dataset.loading = 'false';
        }, 900);
      } catch (error) {
        console.error('Drawer upsell add failed:', error);
        button.textContent = 'Prøv igen';

        window.setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
          button.dataset.loading = 'false';
        }, 1200);
      }
    },
    true
  );
}
